import { useEffect, useState, useContext, useMemo, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import { getProjects, updateProject, assignMemberToProject, createProject, deleteProject } from "../api/projectApi";
import ProjectBudgetPreviewDialog from "../components/ProjectBudgetPreviewDialog";
import { getStoredBudgetOrganizationView } from "../components/BudgetOrganizationSwitcher";
import { formatInr } from "../utils/formatInr";
import { getUsers, getVisibleUsers } from "../api/userApi";
import { DEFAULT_DEPARTMENT } from "../constants/departments";
import { useEnabledDepartments } from "../hooks/useEnabledDepartments";
import { getDepartmentFormHints } from "../constants/departmentFormHints";
import { getStoredDepartmentView } from "../components/DepartmentSwitcher";
import { AuthContext } from "../auth/AuthContext";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Chip,
  LinearProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Paper,
  Button,
  Divider,
  Link,
  TextField,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { toast } from 'react-hot-toast';
import { FiTrash2 } from "react-icons/fi";
import dayjs from "dayjs";

/** Modal dimmer — do not close on backdrop click; use ✕ / Cancel only. */
const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999,
  display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
};

// Enterprise Project Statuses
const PROJECT_STATUSES = [
  { value: "Planning", label: "Planning", color: "#6366f1", bg: "#eef2ff" },
  { value: "Active", label: "Active", color: "#16a34a", bg: "#dcfce7" },
  { value: "On Hold", label: "On Hold", color: "#d97706", bg: "#fef3c7" },
  { value: "Paused", label: "Paused", color: "#ea580c", bg: "#ffedd5" },
  { value: "Completed", label: "Completed", color: "#0891b2", bg: "#cffafe" },
  { value: "Cancelled", label: "Cancelled", color: "#dc2626", bg: "#fee2e2" },
  { value: "Archived", label: "Archived", color: "#64748b", bg: "#f1f5f9" },
];

const NewProjectModal = ({ onClose, onCreated, canPickDepartment, defaultDepartment, departmentOptions = [] }) => {
  const [form, setForm] = useState({
    projectname: "",
    status: "Planning",
    budgetallocated: "",
    startdate: "",
    department: defaultDepartment || DEFAULT_DEPARTMENT,
  });
  const [saving, setSaving] = useState(false);
  const hints = useMemo(() => getDepartmentFormHints(form.department), [form.department]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createProject(form);
      toast.success("Project created!");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create project");
    }
    setSaving(false);
  };

  const inp = { width: "100%", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 8,
    fontSize: "0.9rem", background: "#f9fafb", boxSizing: "border-box", marginTop: 4 };
  const lbl = { fontSize: "0.8rem", fontWeight: 600, color: "#374151", marginTop: 14, display: "block" };

  return (
    <div style={overlayStyle}>
      <div style={{ background: "white", borderRadius: 14, padding: "2rem", width: "100%", maxWidth: 480,
        boxShadow: "0 25px 60px rgba(0,0,0,0.25)" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 800, color: "#111827" }}>New Project</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6b7280" }}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <label style={lbl}>Project Name *</label>
          <input style={inp} value={form.projectname} onChange={e => set("projectname", e.target.value)}
            placeholder={hints.projectName} required />

          <label style={lbl}>Status</label>
          <select style={inp} value={form.status} onChange={e => set("status", e.target.value)}>
            {PROJECT_STATUSES.slice(0, 2).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <label style={lbl}>{hints.budgetLabel}</label>
          <input style={inp} type="number" min={0} value={form.budgetallocated}
            onChange={e => set("budgetallocated", e.target.value)} placeholder={hints.budgetPlaceholder} />

          <label style={lbl}>Start Date</label>
          <input style={inp} type="date" value={form.startdate}
            onChange={e => set("startdate", e.target.value)} />
          <p style={{ fontSize: "0.72rem", color: "#9ca3af", margin: "4px 0 0" }}>
            {hints.startDateHelper}
          </p>

          {canPickDepartment ? (
            <>
              <label style={lbl}>Department</label>
              <select style={inp} value={form.department} onChange={(e) => set("department", e.target.value)}>
                {departmentOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </>
          ) : null}

          <button type="submit" disabled={saving} style={{
            marginTop: 22, width: "100%", padding: "10px", background: "#1272B3",
            color: "white", border: "none", borderRadius: 8, fontWeight: 700,
            fontSize: "0.95rem", cursor: "pointer",
          }}>
            {saving ? "Creating…" : "Create Project"}
          </button>
        </form>
      </div>
    </div>
  );
};

const toDateInput = (d) => {
  if (!d) return "";
  const s = typeof d === "string" ? d : String(d);
  return s.slice(0, 10);
};

/** Values from PostgreSQL json/json_build_object may arrive as objects or JSON strings. */
const parseDbJson = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

const truncateText = (text, maxLen) => {
  if (!text || typeof text !== "string") return "";
  const t = text.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1))}…`;
};

/** Rough lifecycle order for “Status, then name” sort (unknown statuses last). */
const STATUS_SORT_RANK = {
  Active: 0,
  Planning: 1,
  "On Hold": 2,
  Paused: 3,
  Completed: 4,
  Cancelled: 5,
  Archived: 6,
};

const PROJECT_SORT_OPTIONS = [
  { value: "last_activity_desc", label: "Recently updated" },
  { value: "name_asc", label: "Name (A–Z)" },
  { value: "name_desc", label: "Name (Z–A)" },
  { value: "status_then_name", label: "Status, then name" },
  { value: "start_desc", label: "Start date (newest)" },
  { value: "start_asc", label: "Start date (oldest)" },
  { value: "progress_desc", label: "Progress (high → low)" },
  { value: "progress_asc", label: "Progress (low → high)" },
];

const sortProjectsList = (rows, sortBy) => {
  const list = [...(rows || [])];
  const nameKey = (p) => String(p.projectname || p.project_name || p.name || "").trim().toLowerCase();
  const startMs = (p) => {
    const d = p.startdate;
    if (!d) return 0;
    const t = new Date(d).getTime();
    return Number.isNaN(t) ? 0 : t;
  };
  const lastActivityMs = (p) => {
    const v = p.last_activity_at;
    if (v == null || v === "") return 0;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : t;
  };
  const progressRatio = (p) => {
    const total = Number(p.totalissues) || 0;
    if (total === 0) return 0;
    return (Number(p.solvedissues) || 0) / total;
  };
  const statusRank = (p) => {
    const s = (p.status || "").trim();
    return Object.prototype.hasOwnProperty.call(STATUS_SORT_RANK, s) ? STATUS_SORT_RANK[s] : 50;
  };

  const tieName = (a, b) => nameKey(a).localeCompare(nameKey(b), undefined, { sensitivity: "base" });

  switch (sortBy) {
    case "name_asc":
      list.sort((a, b) => tieName(a, b));
      break;
    case "name_desc":
      list.sort((a, b) => tieName(b, a));
      break;
    case "start_desc":
      list.sort((a, b) => startMs(b) - startMs(a) || tieName(a, b));
      break;
    case "start_asc":
      list.sort((a, b) => startMs(a) - startMs(b) || tieName(a, b));
      break;
    case "status_then_name":
      list.sort((a, b) => statusRank(a) - statusRank(b) || tieName(a, b));
      break;
    case "progress_desc":
      list.sort((a, b) => progressRatio(b) - progressRatio(a) || tieName(a, b));
      break;
    case "progress_asc":
      list.sort((a, b) => progressRatio(a) - progressRatio(b) || tieName(a, b));
      break;
    case "last_activity_desc":
    default:
      list.sort((a, b) => lastActivityMs(b) - lastActivityMs(a) || tieName(a, b));
      break;
  }
  return list;
};

/** API returns a JSON array; guard against wrapped or malformed payloads. */
const normalizeProjectsList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.projects)) return data.projects;
  if (data && Array.isArray(data.rows)) return data.rows;
  return [];
};

const PORTFOLIO_VIEW_ROLES = new Set([
  "superadmin",
  "admin",
  "manager",
  "marketing_manager",
  "brand_manager",
  "pr_manager",
  "analyst",
  "product_manager",
  "product",
  "lead",
]);

const newReleaseId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `rn-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/** Normalized releases from DB (newest first). */
const parseReleaseNotesFromDb = (p) => {
  const raw = parseDbJson(p.release_notes);
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r) => r && typeof r === "object")
    .map((r) => ({
      id: String(r.id || "").trim() || newReleaseId(),
      title: String(r.title || "Untitled").trim() || "Untitled",
      body: String(r.body ?? ""),
      created_at: r.created_at ? String(r.created_at) : new Date(0).toISOString(),
      source: r.source === "sprint" ? "sprint" : "manual",
      sprint_id: r.sprint_id != null ? Number(r.sprint_id) : null,
      sprint_name: r.sprint_name ? String(r.sprint_name) : null,
    }))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
};

/** Releases for UI: DB rows, or a read-only view of legacy `stakeholder_features` when JSON is still empty. */
const getDisplayReleaseNotes = (p) => {
  const fromDb = parseReleaseNotesFromDb(p);
  if (fromDb.length > 0) return fromDb;
  if (String(p.stakeholder_features ?? "").trim()) {
    return [
      {
        id: "legacy-view",
        title: "Prior feature list",
        body: String(p.stakeholder_features),
        created_at: "1970-01-01T00:00:00.000Z",
      },
    ];
  }
  return [];
};

const EditProjectModal = ({ project, onClose, onSaved }) => {
  const id = project.projectid || project.project_id || project.id;
  const name = project.projectname || project.project_name || project.name || "";
  const [form, setForm] = useState({
    projectname: name,
    status: project.status || "Active",
    budgetallocated: project.budgetallocated ?? "",
    budgetused: project.budgetused ?? "",
    startdate: toDateInput(project.startdate),
    enddate: toDateInput(project.enddate),
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.projectname?.trim()) {
      toast.error("Project name is required");
      return;
    }
    setSaving(true);
    try {
      await updateProject(id, {
        projectname: form.projectname.trim(),
        status: form.status,
        budgetallocated: Number(form.budgetallocated) || 0,
        budgetused: Number(form.budgetused) || 0,
        startdate: form.startdate || undefined,
        enddate: form.enddate || undefined,
      });
      toast.success("Project updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update project");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete project "${name}"?\n\nThis removes the project, sprints, and related data. This cannot be undone.`)) return;
    const typed = window.prompt(`Type the project name exactly to confirm:\n"${name}"`);
    if (typed !== name) {
      toast.error("Name did not match — delete cancelled");
      return;
    }
    setDeleting(true);
    try {
      await deleteProject(id);
      toast.success("Project deleted");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Cannot delete project");
    }
    setDeleting(false);
  };

  const inp = {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: "0.9rem",
    background: "#f9fafb",
    boxSizing: "border-box",
    marginTop: 4,
  };
  const lbl = { fontSize: "0.8rem", fontWeight: 600, color: "#374151", marginTop: 14, display: "block" };

  return (
    <div style={overlayStyle}>
      <div
        style={{
          background: "white",
          borderRadius: 14,
          padding: "2rem",
          width: "100%",
          maxWidth: 520,
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 800, color: "#111827" }}>Edit project</h2>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6b7280" }}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <label style={lbl}>Project name *</label>
          <input style={inp} value={form.projectname} onChange={(e) => set("projectname", e.target.value)} required />

          <label style={lbl}>Status</label>
          <select style={inp} value={form.status} onChange={(e) => set("status", e.target.value)}>
            {PROJECT_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <p style={{ fontSize: "0.72rem", color: "#9ca3af", margin: "4px 0 0" }}>
            Use "On Hold" or "Paused" to temporarily stop, "Completed" when finished, "Cancelled" if abandoned
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))", gap: "0 12px" }}>
            <div>
              <label style={lbl}>Budget allocated ($)</label>
              <input style={inp} type="number" min={0} value={form.budgetallocated} onChange={(e) => set("budgetallocated", e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Budget used ($)</label>
              <input style={inp} type="number" min={0} value={form.budgetused} onChange={(e) => set("budgetused", e.target.value)} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))", gap: "0 12px" }}>
            <div>
              <label style={lbl}>Start date</label>
              <input style={inp} type="date" value={form.startdate} onChange={(e) => set("startdate", e.target.value)} />
            </div>
            <div>
              <label style={lbl}>End date (optional)</label>
              <input style={inp} type="date" value={form.enddate} onChange={(e) => set("enddate", e.target.value)} />
            </div>
          </div>
          <p style={{ fontSize: "0.72rem", color: "#9ca3af", margin: "4px 0 0" }}>
            End date is optional. Set when project completes or is cancelled.
          </p>

          <button
            type="submit"
            disabled={saving || deleting}
            style={{
              marginTop: 22,
              width: "100%",
              padding: "10px",
              background: "#1272B3",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontWeight: 700,
              fontSize: "0.95rem",
              cursor: "pointer",
            }}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </form>

        <div style={{ marginTop: 28, paddingTop: 20, borderTop: "1px solid #e5e7eb" }}>
          <p style={{ margin: "0 0 10px", fontSize: "0.8rem", color: "#6b7280" }}>Danger zone</p>
          <button
            type="button"
            disabled={saving || deleting}
            onClick={handleDelete}
            style={{
              width: "100%",
              padding: "10px",
              background: "#fef2f2",
              color: "#b91c1c",
              border: "1px solid #fecaca",
              borderRadius: 8,
              fontWeight: 700,
              fontSize: "0.9rem",
              cursor: "pointer",
            }}
          >
            {deleting ? "Deleting…" : "Delete project"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Projects = () => {
  const { role, user: currentUser } = useContext(AuthContext);
  const { selectableDepartments } = useEnabledDepartments();
  const currentRole = (role || "").toLowerCase();
  const isAdmin = ["admin", "superadmin", "manager", "marketing_manager", "brand_manager", "pr_manager", "analyst", "product", "product_manager", "lead"].includes(currentRole);
  const canPickDepartment = ["admin", "superadmin"].includes(currentRole);
  const userDepartment = currentUser?.department || DEFAULT_DEPARTMENT;
  const departmentView = getStoredDepartmentView();
  const defaultProjectDepartment = departmentView || userDepartment;
  const canEditStakeholderFeatures = [
    "admin",
    "superadmin",
    "manager",
    "marketing_manager",
    "brand_manager",
    "pr_manager",
    "analyst",
    "product",
    "product_manager",
    "lead",
  ].includes(currentRole);
  const canEditBudgetLines = [
    "admin",
    "superadmin",
    "manager",
    "marketing_manager",
    "brand_manager",
    "pr_manager",
    "analyst",
    "product",
    "product_manager",
    "lead",
  ].includes(currentRole);

  const [projects, setProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [changingOwner, setChangingOwner] = useState(null); // project id currently editing owner
  const [sortBy, setSortBy] = useState("last_activity_desc");
  const [releaseDialogProjectId, setReleaseDialogProjectId] = useState(null);
  const [newReleaseTitle, setNewReleaseTitle] = useState("");
  const [newReleaseBody, setNewReleaseBody] = useState("");
  const [savingReleaseId, setSavingReleaseId] = useState(null);
  const [releaseTabByProject, setReleaseTabByProject] = useState({});
  /** When set, confirms deletion of a single release: { projectId, releaseId, title } */
  const [deleteReleaseTarget, setDeleteReleaseTarget] = useState(null);
  const [deletingRelease, setDeletingRelease] = useState(false);
  /** Epics sidebar: 0 = Current sprint, 1 = Pipeline */
  const [epicsTabByProject, setEpicsTabByProject] = useState({});
  /** `{ id, name }` when Budget Preview dialog is open */
  const [budgetPreview, setBudgetPreview] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const hasPortfolioView = PORTFOLIO_VIEW_ROLES.has(currentRole);

  const sortedProjects = useMemo(() => sortProjectsList(projects, sortBy), [projects, sortBy]);

  const loadData = useCallback(async () => {
    setLoadError(null);
    try {
      const pRes = await getProjects(
        hasPortfolioView && includeArchived ? { includeArchived: true } : {}
      );
      setProjects(normalizeProjectsList(pRes.data));
    } catch (err) {
      console.error("Projects load error", err);
      setLoadError(err.response?.data?.message || "Failed to load projects");
      toast.error("Could not load projects");
    }

    try {
      const usersFn = ["admin", "superadmin"].includes(currentRole) ? getUsers : getVisibleUsers;
      const uRes = await usersFn();
      const MANAGER_ROLES = ["manager", "marketing_manager", "brand_manager", "pr_manager", "product", "product_manager", "analyst", "lead"];
      const filtered = (uRes.data || []).filter((u) =>
        MANAGER_ROLES.includes((u.role || "").toLowerCase())
      );
      setAllUsers(filtered);
    } catch (err) {
      console.error("Users load error (owner dropdown)", err);
    } finally {
      setLoading(false);
    }
  }, [hasPortfolioView, includeArchived, currentRole]);

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [loadData]);

  const handleUpdate = async (project, field, value) => {
    if (project[field] === value) return;
    try {
      const id = project.projectid || project.project_id || project.id;
      await updateProject(id, { ...project, [field]: value });
      toast.success("Saved!");
      loadData();
    } catch (err) { toast.error("Save failed"); }
  };

  /**
   * Remove a single release entry from a project's release_notes JSONB.
   * Sprint-sourced entries are filtered out at the call-site (we never open
   * the confirm dialog for those because the boot backfill would re-add them).
   *
   * Matching is composite (id, then title + created_at) because legacy DB
   * rows can lack an `id` field — `parseReleaseNotesFromDb` synthesizes a
   * random id in that case, which would otherwise drift between renders.
   */
  const handleConfirmDeleteRelease = async () => {
    if (!deleteReleaseTarget) return;
    const { projectId, releaseId, title, createdAt } = deleteReleaseTarget;
    const p = projects.find(
      (x) => String(x.projectid || x.project_id || x.id) === String(projectId)
    );
    if (!p) {
      setDeleteReleaseTarget(null);
      return;
    }
    const next = parseReleaseNotesFromDb(p).filter((r) => {
      if (r.id && releaseId && r.id === releaseId) return false;
      if (
        title &&
        createdAt &&
        String(r.title || "") === String(title) &&
        String(r.created_at || "") === String(createdAt)
      ) return false;
      return true;
    });
    setDeletingRelease(true);
    try {
      await updateProject(projectId, { release_notes: next });
      toast.success("Release deleted");
      setReleaseTabByProject((prev) => ({ ...prev, [projectId]: 0 }));
      setDeleteReleaseTarget(null);
      await loadData();
    } catch {
      toast.error("Could not delete release");
    } finally {
      setDeletingRelease(false);
    }
  };

  const handleSaveRelease = async () => {
    const pid = releaseDialogProjectId;
    if (!pid) return;
    const title = newReleaseTitle.trim();
    if (!title) {
      toast.error("Release name is required");
      return;
    }
    const p = projects.find((x) => String(x.projectid || x.project_id || x.id) === String(pid));
    if (!p) return;
    const fromDb = parseReleaseNotesFromDb(p);
    const hadLegacyOnly = fromDb.length === 0 && String(p.stakeholder_features ?? "").trim();
    const list = [...fromDb];
    if (hadLegacyOnly) {
      list.push({
        id: newReleaseId(),
        title: "Prior feature list",
        body: String(p.stakeholder_features),
        created_at: new Date(0).toISOString(),
      });
    }
    list.push({
      id: newReleaseId(),
      title,
      body: newReleaseBody.trim(),
      created_at: new Date().toISOString(),
    });
    list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    setSavingReleaseId(pid);
    try {
      const payload = { release_notes: list };
      if (hadLegacyOnly) payload.stakeholder_features = null;
      await updateProject(pid, payload);
      toast.success("Release notes saved");
      setReleaseDialogProjectId(null);
      setNewReleaseTitle("");
      setNewReleaseBody("");
      setReleaseTabByProject((prev) => ({ ...prev, [pid]: 0 }));
      await loadData();
    } catch {
      toast.error("Could not save release notes");
    }
    setSavingReleaseId(null);
  };

  if (loading) return <CircularProgress sx={{ m: 10 }} />;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: { xs: 2, sm: 3 }, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#2C63AE" }}>
              Projects Overview
            </Typography>
            {!loading && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {projects.length} project{projects.length === 1 ? "" : "s"}
                {includeArchived ? " (including archived & cancelled)" : ""}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {hasPortfolioView && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={includeArchived}
                    onChange={(e) => setIncludeArchived(e.target.checked)}
                  />
                }
                label="Show archived & cancelled"
                sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
              />
            )}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="projects-sort-label">Sort projects</InputLabel>
              <Select
                labelId="projects-sort-label"
                label="Sort projects"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {PROJECT_SORT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isAdmin && (
              <button onClick={() => setShowNewProject(true)} style={{
                background: "#1272B3", color: "white", border: "none", padding: "10px 20px",
                borderRadius: 8, fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                + New Project
              </button>
            )}
          </Stack>
        </Box>

        {loadError && (
          <Box sx={{ textAlign: "center", py: 4, color: "#b91c1c" }}>
            {loadError}
          </Box>
        )}

        {!loadError && projects.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8, color: "#6b7280" }}>
            No projects yet. {isAdmin && "Click \"+ New Project\" to get started."}
            {hasPortfolioView && !includeArchived && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Try enabling &quot;Show archived &amp; cancelled&quot; if you expect older projects.
              </Typography>
            )}
          </Box>
        )}
        
        {sortedProjects.map((p, projectIdx) => {
          const id = p.projectid || p.project_id || p.projectId || p.id;
          const name = p.projectname || p.project_name || p.name || "Unnamed Project";
          const prog = p.totalissues > 0 ? ((p.solvedissues || 0) / p.totalissues) * 100 : 0;
          const activeSprint = parseDbJson(p.active_sprint);
          const rawEpicsActive = parseDbJson(p.epics_active_preview);
          const rawEpicsPipeline = parseDbJson(p.epics_pipeline_preview);
          const epicsActive = Array.isArray(rawEpicsActive) ? rawEpicsActive : [];
          const epicsPipeline = Array.isArray(rawEpicsPipeline) ? rawEpicsPipeline : [];
          const openEpicsCount = Number(p.open_epics_count) || 0;
          const openEpicsOnActiveSprint = Number(p.open_epics_in_active_sprint_count) || 0;
          const openEpicsPipelineTotal = Math.max(0, openEpicsCount - openEpicsOnActiveSprint);
          const defaultEpicsTab = activeSprint?.id != null ? 0 : 1;
          const epicsTabRaw = epicsTabByProject[id];
          const epicsTabIdx =
            epicsTabRaw !== undefined ? Math.min(Math.max(0, epicsTabRaw), 1) : defaultEpicsTab;
          const urgentOpen = Number(p.urgent_bugs_open) || 0;
          const boardTo =
            activeSprint?.id != null
              ? `/sprint-board?projectId=${encodeURIComponent(String(id))}&sprintId=${encodeURIComponent(String(activeSprint.id))}`
              : `/sprint-board?projectId=${encodeURIComponent(String(id))}`;
          const atGlanceSummary = (() => {
            const goal = activeSprint?.goal?.trim();
            if (goal) return truncateText(goal, 360);
            if (activeSprint?.sprint_name)
              return "Set a sprint goal on the sprint board for a short executive summary.";
            if (p.current_sprint)
              return `No active sprint in the planner; issues may still reference “${p.current_sprint}”.`;
            return "No active sprint yet. Open the sprint board when delivery starts.";
          })();

          const displayReleases = getDisplayReleaseNotes(p);
          const tabRaw = releaseTabByProject[id] ?? 0;
          const releaseTabIdx = displayReleases.length
            ? Math.min(Math.max(0, tabRaw), displayReleases.length - 1)
            : 0;

          return (
            <Card
              key={id != null && id !== "" ? String(id) : `project-row-${projectIdx}`}
              elevation={0}
              sx={{
                mb: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                <Grid container spacing={2} sx={{ alignItems: "stretch", width: "100%" }}>
                  <Grid size={{ xs: 12, md: 8 }} sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1.25,
                        flexWrap: "wrap",
                        pb: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
                        {name}
                      </Typography>
                      {isAdmin && (
                        <Button variant="outlined" size="small" onClick={() => setEditProject(p)} sx={{ textTransform: "none", fontWeight: 600 }}>
                          Edit project
                        </Button>
                      )}
                      {(() => {
                        const statusMeta = PROJECT_STATUSES.find((s) => s.value === p.status) || { color: "#64748b", bg: "#f1f5f9" };
                        return (
                          <Chip
                            label={p.status || "Active"}
                            size="small"
                            sx={{ background: statusMeta.bg, color: statusMeta.color, fontWeight: 600 }}
                          />
                        );
                      })()}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: "auto", fontWeight: 600 }}>
                        {id}
                      </Typography>
                    </Box>

                    <Paper
                      variant="outlined"
                      sx={{
                        p: { xs: 1.25, sm: 1.5 },
                        mb: 1.5,
                        borderRadius: 1.5,
                        bgcolor: "#f8fafc",
                        borderColor: "#e2e8f0",
                        overflow: "hidden",
                      }}
                    >
                      {urgentOpen > 0 && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                          <Chip
                            label={`${urgentOpen} Blocker / Critical (open)`}
                            size="small"
                            color="error"
                            variant="filled"
                            sx={{ fontWeight: 700, height: 22, fontSize: "0.7rem" }}
                          />
                        </Box>
                      )}

                      <Box
                        sx={{
                          mb: 1.25,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: "common.white",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: "0.8125rem" }}>
                          {atGlanceSummary}
                        </Typography>
                      </Box>

                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }} gap={1}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "#64748b", letterSpacing: 0.4 }}>
                          Release notes
                        </Typography>
                        {canEditStakeholderFeatures && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setReleaseDialogProjectId(id);
                              setNewReleaseTitle("");
                              setNewReleaseBody("");
                            }}
                            disabled={savingReleaseId === id}
                            sx={{ textTransform: "none", fontWeight: 700, py: 0.25, px: 1, fontSize: "0.75rem", flexShrink: 0 }}
                          >
                            Add release
                          </Button>
                        )}
                      </Stack>

                      <Box sx={{ mt: 0 }}>
                        {displayReleases.length === 0 ? (
                          <Box
                            sx={{
                              height: 252,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 1.25,
                              bgcolor: "common.white",
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                              No release notes yet.
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              height: 252,
                              display: "flex",
                              flexDirection: "column",
                              borderRadius: 1.25,
                              bgcolor: "common.white",
                              border: "1px solid",
                              borderColor: "divider",
                              overflow: "hidden",
                            }}
                          >
                            <Tabs
                              value={releaseTabIdx}
                              onChange={(_, v) => setReleaseTabByProject((prev) => ({ ...prev, [id]: v }))}
                              variant="scrollable"
                              scrollButtons="auto"
                              sx={{
                                flexShrink: 0,
                                minHeight: 40,
                                borderBottom: 1,
                                borderColor: "divider",
                                "& .MuiTab-root": {
                                  textTransform: "none",
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  minHeight: 40,
                                  py: 0.5,
                                  px: 1,
                                },
                              }}
                            >
                              {displayReleases.map((r) => (
                                <Tab
                                  key={r.id}
                                  label={r.title.length > 22 ? `${r.title.slice(0, 20)}…` : r.title}
                                />
                              ))}
                            </Tabs>
                            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1.25 }}>
                              {(() => {
                                const activeRelease = displayReleases[releaseTabIdx];
                                if (!activeRelease) return null;
                                const isSprintSourced = activeRelease.source === "sprint";
                                const isLegacyView = activeRelease.id === "legacy-view";
                                const canDelete = canEditStakeholderFeatures && !isSprintSourced && !isLegacyView;
                                return (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                      mb: 0.75,
                                    }}
                                  >
                                    {isSprintSourced ? (
                                      <Box
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          px: 0.75,
                                          py: 0.25,
                                          borderRadius: 999,
                                          bgcolor: "#EAF6FD",
                                          color: "#2C63AE",
                                          border: "1px solid #a9ddf5",
                                          fontSize: "0.65rem",
                                          fontWeight: 800,
                                          letterSpacing: "0.04em",
                                          textTransform: "uppercase",
                                        }}
                                        title={
                                          activeRelease.sprint_name
                                            ? `Auto-published from sprint: ${activeRelease.sprint_name}`
                                            : "Auto-published from a sprint release"
                                        }
                                      >
                                        🚀 Sprint{activeRelease.sprint_name ? ` · ${activeRelease.sprint_name}` : ""}
                                      </Box>
                                    ) : (
                                      <span />
                                    )}
                                    {canDelete ? (
                                      <Tooltip title="Delete this release">
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            setDeleteReleaseTarget({
                                              projectId: id,
                                              releaseId: activeRelease.id,
                                              title: activeRelease.title,
                                              createdAt: activeRelease.created_at,
                                            })
                                          }
                                          sx={{
                                            color: "#94a3b8",
                                            "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" },
                                            p: 0.5,
                                          }}
                                        >
                                          <FiTrash2 size={14} />
                                        </IconButton>
                                      </Tooltip>
                                    ) : isSprintSourced && canEditStakeholderFeatures ? (
                                      <Tooltip title="Sprint releases auto-publish from the Sprint board and cannot be deleted here">
                                        <span>
                                          <IconButton size="small" disabled sx={{ p: 0.5, color: "#cbd5e1" }}>
                                            <FiTrash2 size={14} />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    ) : null}
                                  </Box>
                                );
                              })()}
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6, fontSize: "0.8125rem", color: "#334155" }}
                              >
                                {displayReleases[releaseTabIdx]?.body?.trim()
                                  ? displayReleases[releaseTabIdx].body
                                  : "—"}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                          <Link component={RouterLink} to="/issues" underline="hover">
                            Open Issues
                          </Link>
                          {" · "}
                          <Link component={RouterLink} to={boardTo} underline="hover">
                            Sprint board
                          </Link>
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 1.25 }} />
                      <Button
                        component={RouterLink}
                        to={boardTo}
                        variant="contained"
                        size="small"
                        sx={{ textTransform: "none", fontWeight: 800, px: 2, bgcolor: "#1272B3", boxShadow: "none" }}
                      >
                        Open sprint board
                      </Button>
                    </Paper>

                    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 1 }}>
                      <LinearProgress variant="determinate" value={prog} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 600 }}>
                        {p.solvedissues || 0}/{p.totalissues || 0}
                      </Typography>
                    </Stack>

                    {isAdmin && (
                      <Box sx={{ mt: 1.25 }}>
                        <Typography variant="caption" sx={{ mb: 0.5, display: "block", fontWeight: 700, color: "#64748b" }}>
                          Owner
                        </Typography>
                        {(() => {
                          const ownerId = p.owner_id ?? null;
                          const ownerUser = ownerId ? allUsers.find(u => String(u.id) === String(ownerId)) : null;
                          const isChanging = changingOwner === id;
                          return isChanging ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FormControl size="small" sx={{ flex: 1 }}>
                                <Select
                                  autoFocus
                                  defaultValue={ownerId || ""}
                                  displayEmpty
                                  onChange={(e) => {
                                    assignMemberToProject(id, e.target.value)
                                      .then(() => { toast.success('Owner updated!'); loadData(); setChangingOwner(null); })
                                      .catch(() => toast.error('Failed to update owner'));
                                  }}
                                >
                                  <MenuItem value="" disabled><em>Select manager…</em></MenuItem>
                                  {allUsers.map(u => (
                                    <MenuItem key={u.id} value={u.id}>
                                      {u.full_name || u.email}{u.id === currentUser?.id ? ' (you)' : ''} · {u.role}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <button onClick={() => setChangingOwner(null)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1rem' }}>✕</button>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600 }}>
                                {ownerUser ? `${ownerUser.full_name || ownerUser.email} · ${ownerUser.role}` : 'Not assigned'}
                              </Typography>
                              <button
                                onClick={() => setChangingOwner(id)}
                                style={{ fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px',
                                  borderRadius: 6, border: '1px solid #a9ddf5', background: '#EAF6FD',
                                  color: '#2C63AE', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                Change
                              </button>
                            </Box>
                          );
                        })()}
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex", minWidth: 0 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        width: "100%",
                        alignSelf: "stretch",
                        bgcolor: "#f1f5f9",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.5,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                      }}
                    >
                      <Typography variant="overline" sx={{ fontWeight: 800, color: "#64748b", letterSpacing: 0.6 }}>
                        Epics
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          mb: 1.5,
                          height: 288,
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: 1,
                          bgcolor: "common.white",
                          border: "1px solid",
                          borderColor: "divider",
                          overflow: "hidden",
                        }}
                      >
                        <Tabs
                          value={epicsTabIdx}
                          onChange={(_, v) => setEpicsTabByProject((prev) => ({ ...prev, [id]: v }))}
                          variant="fullWidth"
                          sx={{
                            flexShrink: 0,
                            minHeight: 42,
                            borderBottom: 1,
                            borderColor: "divider",
                            "& .MuiTab-root": { textTransform: "none", fontWeight: 700, fontSize: "0.75rem", minHeight: 42, py: 0.65 },
                          }}
                        >
                          <Tab label="Current" />
                          <Tab label="Pipeline" />
                        </Tabs>
                        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                          {epicsTabIdx === 0 ? (
                            <>
                              {activeSprint?.id == null ? (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 1.25, lineHeight: 1.45, fontSize: "0.8125rem" }}>
                                  No active sprint — start or activate a sprint on the board to scope current epics.
                                </Typography>
                              ) : epicsActive.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 1.25, lineHeight: 1.45, fontSize: "0.8125rem" }}>
                                  No open epics assigned to the active sprint.
                                </Typography>
                              ) : (
                                <List disablePadding>
                                  {epicsActive.map((ep, idx) => (
                                    <ListItem key={ep.id} disablePadding divider={idx < epicsActive.length - 1}>
                                      <ListItemButton
                                        component={RouterLink}
                                        to={`/issues?openIssueId=${encodeURIComponent(String(ep.id))}`}
                                        sx={{ py: 1.15, alignItems: "flex-start", px: 1.35 }}
                                      >
                                        <ListItemText
                                          primary={ep.title || "Untitled"}
                                          secondary={ep.status ? `Status: ${ep.status}` : null}
                                          primaryTypographyProps={{ fontWeight: 700, color: "primary.dark", fontSize: "0.875rem" }}
                                          secondaryTypographyProps={{ variant: "caption", sx: { mt: 0.35 } }}
                                        />
                                      </ListItemButton>
                                    </ListItem>
                                  ))}
                                </List>
                              )}
                              {activeSprint?.id != null && openEpicsOnActiveSprint > epicsActive.length ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 1.25, pb: 1, pt: 0 }}>
                                  +{openEpicsOnActiveSprint - epicsActive.length} more on sprint —{" "}
                                  <Link component={RouterLink} to="/issues" underline="hover">
                                    Open Issues
                                  </Link>
                                </Typography>
                              ) : null}
                            </>
                          ) : (
                            <>
                              {epicsPipeline.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 1.25, lineHeight: 1.45, fontSize: "0.8125rem" }}>
                                  {activeSprint?.id != null
                                    ? "No backlog epics outside the active sprint."
                                    : "No open epics on this project."}
                                </Typography>
                              ) : (
                                <List disablePadding>
                                  {epicsPipeline.map((ep, idx) => (
                                    <ListItem key={ep.id} disablePadding divider={idx < epicsPipeline.length - 1}>
                                      <ListItemButton
                                        component={RouterLink}
                                        to={`/issues?openIssueId=${encodeURIComponent(String(ep.id))}`}
                                        sx={{ py: 1.15, alignItems: "flex-start", px: 1.35 }}
                                      >
                                        <ListItemText
                                          primary={ep.title || "Untitled"}
                                          secondary={ep.status ? `Status: ${ep.status}` : null}
                                          primaryTypographyProps={{ fontWeight: 700, color: "primary.dark", fontSize: "0.875rem" }}
                                          secondaryTypographyProps={{ variant: "caption", sx: { mt: 0.35 } }}
                                        />
                                      </ListItemButton>
                                    </ListItem>
                                  ))}
                                </List>
                              )}
                              {openEpicsPipelineTotal > epicsPipeline.length ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 1.25, pb: 1, pt: 0 }}>
                                  +{openEpicsPipelineTotal - epicsPipeline.length} more in backlog —{" "}
                                  <Link component={RouterLink} to="/issues" underline="hover">
                                    Open Issues
                                  </Link>
                                </Typography>
                              ) : null}
                            </>
                          )}
                        </Box>
                      </Box>

                      <Divider sx={{ my: 0.5 }} />

                      <Typography variant="overline" sx={{ fontWeight: 800, color: "#64748b", letterSpacing: 0.6, mt: 0.5 }}>
                        Dates
                      </Typography>
                      {isAdmin && (
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
                          <DatePicker
                            label="Start date"
                            value={p.startdate ? dayjs(p.startdate) : null}
                            onChange={(v) => handleUpdate(p, "startdate", v?.format("YYYY-MM-DD"))}
                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                          />
                          <DatePicker
                            label="End date"
                            value={p.enddate ? dayjs(p.enddate) : null}
                            onChange={(v) => handleUpdate(p, "enddate", v?.format("YYYY-MM-DD"))}
                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                          />
                        </Stack>
                      )}
                      {!isAdmin && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                          {p.startdate ? dayjs(p.startdate).format("DD MMM YYYY") : "Not set"} →{" "}
                          {p.enddate ? dayjs(p.enddate).format("DD MMM YYYY") : "Ongoing"}
                        </Typography>
                      )}

                      <Typography variant="overline" sx={{ fontWeight: 800, color: "#64748b", letterSpacing: 0.6, mt: 1.5 }}>
                        Budget
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700, color: "#0f172a" }}>
                        Current Month : {formatInr(Number(p.budget_monthly_burn_inr ?? 0))} INR
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}>
                        Recurring run-rate for the month plus spends logged for this calendar month. Legacy fields stay in Edit project.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setBudgetPreview({
                          id,
                          name,
                          yearlyBudget: Number(p.budgetallocated || 0),
                          department: p.department,
                        })}
                        sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
                      >
                        Budget Preview
                      </Button>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <ProjectBudgetPreviewDialog
        open={Boolean(budgetPreview)}
        onClose={() => setBudgetPreview(null)}
        projectId={budgetPreview?.id}
        projectName={budgetPreview?.name}
        projectDepartment={budgetPreview?.department}
        organizationView={getStoredBudgetOrganizationView()}
        yearlyBudget={budgetPreview?.yearlyBudget ?? 0}
        canEdit={canEditBudgetLines}
        onSaved={loadData}
      />

      <Dialog
        open={Boolean(releaseDialogProjectId)}
        onClose={() => {
          if (!savingReleaseId) setReleaseDialogProjectId(null);
        }}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2 } } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Add release notes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {(() => {
              const pr = projects.find(
                (x) => String(x.projectid || x.project_id || x.id) === String(releaseDialogProjectId)
              );
              return pr ? String(pr.projectname || pr.project_name || pr.name || "").trim() || "Project" : "";
            })()}
          </Typography>
          <TextField
            autoFocus
            label="Release name"
            fullWidth
            required
            value={newReleaseTitle}
            onChange={(e) => setNewReleaseTitle(e.target.value)}
            placeholder="e.g. v2.1 or April 2026"
            margin="dense"
            size="small"
          />
          <TextField
            label="Notes / feature list"
            fullWidth
            multiline
            minRows={6}
            value={newReleaseBody}
            onChange={(e) => setNewReleaseBody(e.target.value)}
            margin="dense"
            size="small"
            placeholder="What shipped or what changed in this release."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setReleaseDialogProjectId(null)}
            disabled={Boolean(savingReleaseId)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRelease}
            disabled={Boolean(savingReleaseId) || !newReleaseTitle.trim()}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {savingReleaseId ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteReleaseTarget)}
        onClose={() => {
          if (!deletingRelease) setDeleteReleaseTarget(null);
        }}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2 } } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Delete release?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will remove the release tab
            {deleteReleaseTarget?.title ? (
              <>
                {" "}<strong style={{ color: "#0f172a" }}>{deleteReleaseTarget.title}</strong>
              </>
            ) : null}
            {" "}from this project. The action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteReleaseTarget(null)}
            disabled={deletingRelease}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteRelease}
            disabled={deletingRelease}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {deletingRelease ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {showNewProject && (
        <NewProjectModal
          onClose={() => setShowNewProject(false)}
          onCreated={loadData}
          canPickDepartment={canPickDepartment}
          defaultDepartment={canPickDepartment ? defaultProjectDepartment : userDepartment}
          departmentOptions={selectableDepartments}
        />
      )}
      {editProject && (
        <EditProjectModal
          key={editProject.projectid || editProject.project_id || editProject.id}
          project={editProject}
          onClose={() => setEditProject(null)}
          onSaved={loadData}
        />
      )}
    </LocalizationProvider>
  );
};

export default Projects;
